import * as React from 'react';

import { useNavigate } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import BackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Close';
import { NavLink } from 'react-router-dom';
import { useWindowDimensions, copyArrayWithIndexChanged } from '../Utils';

import { GridRowModesModel, GridRowModes, GridActionsCellItem, GridEventListener, GridRowId } from '@mui/x-data-grid';
import { DataGrid, GridColDef, GridRowEditStopReasons, GridRowModel } from '@mui/x-data-grid';

import { Breadcrumbs, Box, Button, Stack, Typography } from '@mui/material';
import { BreadCrumbProps } from "../subs/PageAlternate";
import { User, UserAccessLevelStrings, UserStatusStrings, getAllUsers, updateUserData } from '../types/User';
import { Company, getCompanies } from '../types/Company';

import { fromDateStr, formatDate } from '../Utils';

import { AlertProps } from '@mui/material/Alert';
import { useSnackbar } from '../../contexts/SnackBarContext';

// global theming ruins the back button, disable it here
const backTheme = createTheme({
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                },
            },
        }
    }
});

interface UserNamedCompany extends User {
    companyName?: string;
};


const breadcrumbs: BreadCrumbProps[] = [
    { to: "/Administration", text: "Administration" },
    { to: "/DBFunctionPage", text: "Functions" },
];

export const AllUsersAdmin = () => {
    return <AllUsersPage backButton
        breadcrumbs={breadcrumbs} />
}

const AllUsersAdminView = ({ title }:
    {
        title: string
    }) => {
    const { showSnackbar } = useSnackbar();
    const [allUsers, setAllUsers] = React.useState<User[]>([]);
    const [allCompanies, setAllCompanies] = React.useState<Company[]>([]);
    const [allRows, setAllRows] = React.useState<UserNamedCompany[]>([]);
    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        const fetchAllUsers = async () => {
            const allUsersResponse = await getAllUsers(new AbortController());
            if (allUsersResponse.failed) {
                showUserFeedback(allUsersResponse.errorText);
                return;
            }
            setAllUsers(allUsersResponse.result);

            const allCompaniesResponse = await getCompanies(new AbortController());
            if (allCompaniesResponse.failed) {
                showUserFeedback(allCompaniesResponse.errorText);
                return;
            }
            setAllCompanies(allCompaniesResponse.result);
        }
        fetchAllUsers();
    }, [showUserFeedback]);

    React.useEffect(() => {
        let allRowsLoc = allUsers.map(user => {
            const company = allCompanies.find(c => c.id === user.companyId);
            return {
                ...user,
                companyName: company ? company.name : 'No Company'
            } as UserNamedCompany;
        });
        setAllRows(allRowsLoc);
    }, [allUsers, allCompanies]);

    const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
        }
    };

    const handleEditClick = (id: GridRowId) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    };

    const handleCancelClick = (id: GridRowId) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        });
    };

    const handleSaveClick = (id: GridRowId) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    };

    const processRowUpdate = (newRow: GridRowModel) => {
        const changedData = allUsers.find(row => row.id === newRow.id);
        if(!changedData) {
            showUserFeedback('Row not found', 'error');
            return newRow;
        }
        changedData.status = newRow.status as number;
        changedData.accessLevel = newRow.accessLevel as number;
        changedData.companyId = newRow.companyId as number;
        saveHandler(changedData);
        setAllRows(allRows.map((row) => (row.id === newRow.id ? newRow as UserNamedCompany : row)));
        return newRow;
    };


    const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };


    const saveHandler = async (changedData: User) => {
        const updateUserRequest = await updateUserData(changedData, new AbortController());

        if (updateUserRequest.failed) {
            showUserFeedback(updateUserRequest.errorText);
            return;
        }
        const updateUser = updateUserRequest.result;
        let allUsersLoc = copyArrayWithIndexChanged(allUsers, allUsers.findIndex(tc => tc.id === updateUser.id), updateUser);
        setAllUsers(allUsersLoc);
    }

    const columns: GridColDef[] = [
        {
            field: 'accessLevel',
            headerName: 'Access Level',
            flex: 1,
            editable: true,
            valueFormatter: (value?: number) => {
                if (value == null) {
                    return '';
                }
                return UserAccessLevelStrings[value];
            },
            valueGetter: (value, row) => {
                if (value == null) {
                    return '';
                }
                return UserAccessLevelStrings[value];
            },
            valueSetter: (value, row) => {
                let accessLevel = 0;
                if (UserAccessLevelStrings.find(v => v === value) !== null) {
                    for (let i = 0; i < UserAccessLevelStrings.length; i++) {
                        if (UserAccessLevelStrings[i] === value) {
                            accessLevel = i;
                            break;
                        }
                    }
                }
                return { ...row, accessLevel: accessLevel };
            },
            type: 'singleSelect',
            valueOptions: UserAccessLevelStrings
        },
        {
            field: 'name',
            headerName: 'Name',
            flex: 2
        },
        {
            field: 'email',
            headerName: 'E-Mail',
            flex: 3,
        },
        {
            field: 'companyId',
            headerName: 'Company',
            flex: 3,
            editable: true,
         /*   valueFormatter: (value?: number) => {
                if (value == null) {
                    return '';
                }
                return allCompanies.find(c => c.id === value)?.name || '';
            },*/
            valueGetter: (value, row) => {
                if (value == null) {
                    return '';
                }
                return allCompanies.find(c => c.id === value)?.name || '';
            },
            valueSetter: (value, row) => {
                let companyId = 0;
                if (allCompanies.find(c => c.name === value) !== null) {
                    for (let i = 0; i < allCompanies.length; i++) {
                        if (allCompanies[i].name === value) {
                            companyId = allCompanies[i].id;
                            break;
                        }
                    }
                }
                return { ...row, companyId: companyId };
            },
            type: 'singleSelect',
            valueOptions: allCompanies.map((c: Company) => c.name as string),
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 3,
            editable: true,
            valueFormatter: (value?: number) => {
                if (value == null) {
                    return '';
                }
                return UserStatusStrings[value];
            },
            valueGetter: (value, row) => {
                if (value == null) {
                    return '';
                }
                return UserStatusStrings[value];
            },
            valueSetter: (value, row) => {
                let status = 0;
                if (UserStatusStrings.find(v => v === value) !== null) {
                    for (let i = 0; i < UserStatusStrings.length; i++) {
                        if (UserStatusStrings[i] === value) {
                            status = i;
                            break;
                        }
                    }
                }
                return { ...row, status: status };
            },
            type: 'singleSelect',
            valueOptions: UserStatusStrings
        },
        {
            field: 'lastLoginTime',
            headerName: 'Last Login',
            valueGetter: (value, row) => {
                const lastLoginTimeStr = row.lastLoginTime ? fromDateStr(row.lastLoginTime) : null;
                return `${lastLoginTimeStr ? formatDate(lastLoginTimeStr) : ''}`;
            },
            flex: 3
        },
        {
            field: 'id'
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

                if (isInEditMode) {
                    return [
                        <GridActionsCellItem
                            icon={<SaveIcon />}
                            label="Save"
                            onClick={handleSaveClick(id)}
                        />,
                        <GridActionsCellItem
                            icon={<CancelIcon />}
                            label="Cancel"
                            className="textPrimary"
                            onClick={handleCancelClick(id)}
                            color="inherit"
                        />,
                    ];
                }

                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label="Edit"
                        className="textPrimary"
                        onClick={handleEditClick(id)}
                        color="inherit"
                    />,
                ];
            },
        },
    ];

    return <React.Fragment>
        <Typography variant='h4'>{title}</Typography>
        <Box sx={{ width: '100%' }} marginTop={15}>
            <DataGrid
                columns={columns}
                rows={allRows}
                editMode="row"
                rowModesModel={rowModesModel}
                columnVisibilityModel={{ id: false }}
                sx={{
                    "& .selectRowClass": {
                        color: 'primary.main'
                    },
                }}

                onRowModesModelChange={handleRowModesModelChange}
                onRowEditStop={handleRowEditStop}
                processRowUpdate={processRowUpdate}
                slotProps={{
                    toolbar: { setAllRows, setRowModesModel },
                }}

            />
        </Box>
    </React.Fragment>
}

const AllUsersPage = ({ backButton, breadcrumbs }:
    {
        backButton?: boolean,
        breadcrumbs?: BreadCrumbProps[]
    }) => {
    const navigate = useNavigate();
    const { height, width } = useWindowDimensions();

    const maxHeight = height - 180 + 'px';

    const backButtonHandler = () => navigate(-1);

    const visibleElement = { visibility: "visible" };
    const hiddenElement = { visibility: "hidden" };

    return <>
        <Stack direction="row" spacing={200}>
            <ThemeProvider theme={backTheme}>
                <Button startIcon={<BackIcon />} size="small" onClick={backButtonHandler} sx={backButton ? visibleElement : hiddenElement}>back</Button>
            </ThemeProvider>
            {breadcrumbs && <Breadcrumbs separator="|">
                {breadcrumbs.map((crumb, index) => (
                    <NavLink key={index} to={crumb.to}>{crumb.text}</NavLink>
                ))}
            </Breadcrumbs>}
        </Stack>

        <Box sx={{ flexGrow: 1, width:"100%" }} style={{ maxHeight: maxHeight, overflow: 'auto' }}>
            {<AllUsersAdminView title={'All Users'} />}
        </Box>
    </>
}
